import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Download, Play, Image, Music, Film, FileText, Eye, Trash2, Filter } from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = [
  { id: 'all', label: 'Tümü', icon: Filter },
  { id: 'video', label: 'Videolar', icon: Film },
  { id: 'image', label: 'Görseller', icon: Image },
  { id: 'audio', label: 'Sesler', icon: Music },
  { id: 'text', label: 'Metinler', icon: FileText },
];

export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    let params = [];
    if (filter !== 'all') {
      if (filter === 'text') params.push('content_type=caption');
      else params.push(`content_type=${filter}`);
    }
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    params.push('limit=100');
    api.get(`/content/library?${params.join('&')}`)
      .then(r => { setItems(r.data.items); setTotal(r.data.total); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const handleDelete = async (id) => {
    try { await api.delete(`/content/library/${id}`); toast.success('Silindi'); load(); }
    catch { toast.error('Silme başarısız'); }
  };

  const mediaUrl = (item) => {
    if (!item?.url) return '';
    // Use streaming endpoint for video/audio for better playback
    const url = item.url;
    if (url.includes('/video/') || url.includes('/audio/')) {
      return `${backendUrl}${url.replace('/api/media/', '/api/media/stream/')}`;
    }
    return `${backendUrl}${url}`;
  };

  const isVideo = (item) => item.type === 'video' || item.url?.includes('/video/');
  const isImage = (item) => item.type === 'image' || item.url?.includes('/images/');
  const isAudio = (item) => item.type === 'audio' || item.url?.includes('/audio/');

  const getTypeLabel = (item) => {
    if (isVideo(item)) return 'Video';
    if (isImage(item)) return 'Görsel';
    if (isAudio(item)) return 'Ses';
    return item.type?.replace('_', ' ') || 'Metin';
  };

  const getTypeColor = (item) => {
    if (isVideo(item)) return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
    if (isImage(item)) return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    if (isAudio(item)) return 'bg-green-500/10 text-green-400 border-green-500/20';
    return 'bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20';
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Portfolyo yükleniyor...</div>;

  return (
    <div data-testid="portfolio-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Portfolyo</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Tüm üretimleriniz tek panelde — {total} varlık</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Ara..." className="bg-[#141414] border-[#262626] text-white w-56 h-9" data-testid="portfolio-search" />
          <Button variant="outline" onClick={load} className="border-[#262626] text-[#A3A3A3] hover:text-white h-9 px-3"><Search size={16} /></Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`pf-filter-${f.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-ibm transition-colors duration-200 ${
              filter === f.id ? 'bg-white text-black' : 'bg-[#141414] text-[#A3A3A3] border border-[#262626] hover:border-[#404040]'}`}>
            <f.icon size={12} /> {f.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20"><Film size={48} className="mx-auto text-[#262626] mb-4" /><p className="text-[#737373] font-ibm">Henüz üretim yok</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map(item => (
            <Card key={item.id} className="bg-[#141414] border-[#262626] hover:border-[#404040] transition-colors duration-200 overflow-hidden group" data-testid={`pf-card-${item.id}`}>
              {/* Preview */}
              <div className="relative aspect-video bg-[#0A0A0A] flex items-center justify-center cursor-pointer" onClick={() => setSelected(item)}>
                {isVideo(item) && item.url ? (
                  <video src={mediaUrl(item)} className="w-full h-full object-cover" muted preload="none"
                    poster={item.metadata?.poster ? `${backendUrl}${item.metadata.poster}` : undefined} />
                ) : isImage(item) && item.url ? (
                  <img src={`${backendUrl}${item.url}`} alt="" className="w-full h-full object-cover" />
                ) : isAudio(item) ? (
                  <Music size={32} className="text-[#262626]" />
                ) : (
                  <FileText size={32} className="text-[#262626]" />
                )}
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center">
                  <Eye size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                {isVideo(item) && <div className="absolute bottom-2 left-2"><Play size={16} className="text-white drop-shadow" /></div>}
              </div>
              <CardContent className="p-3">
                <div className="flex items-start justify-between mb-1.5">
                  <Badge variant="outline" className={`text-xs ${getTypeColor(item)}`}>{getTypeLabel(item)}</Badge>
                  <div className="flex gap-0.5">
                    {item.url && <a href={`${backendUrl}${item.url}`} download onClick={e => e.stopPropagation()} className="p-1 text-[#525252] hover:text-[#FACC15]"><Download size={12} /></a>}
                    <button onClick={() => handleDelete(item.id)} className="p-1 text-[#525252] hover:text-red-400"><Trash2 size={12} /></button>
                  </div>
                </div>
                <p className="text-xs font-ibm text-white line-clamp-1">{item.title}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-[#525252] font-jetbrains">{new Date(item.created_at).toLocaleDateString('tr-TR')}</span>
                  {item.metadata?.model && <span className="text-xs text-[#525252] font-jetbrains">{item.metadata.model}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-outfit text-white">{selected?.title}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              {isVideo(selected) && selected.url && (
                <video controls className="w-full rounded-lg border border-[#262626]" data-testid="pf-video-player">
                  <source src={mediaUrl(selected)} type="video/mp4" />
                </video>
              )}
              {isImage(selected) && selected.url && (
                <img src={`${backendUrl}${selected.url}`} alt="" className="w-full rounded-lg border border-[#262626]" />
              )}
              {isAudio(selected) && selected.url && (
                <audio controls className="w-full" src={mediaUrl(selected)} />
              )}
              {selected.content && (
                <pre className="text-sm text-[#A3A3A3] font-ibm whitespace-pre-wrap bg-[#0A0A0A] p-4 rounded-md border border-[#1A1A1A] max-h-[200px] overflow-y-auto">{selected.content}</pre>
              )}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Badge variant="outline" className={getTypeColor(selected)}>{getTypeLabel(selected)}</Badge>
                  <span className="text-xs text-[#525252] font-jetbrains">{new Date(selected.created_at).toLocaleString('tr-TR')}</span>
                </div>
                {selected.url && (
                  <a href={`${backendUrl}${selected.url}`} download className="text-sm text-[#FACC15] hover:text-white font-ibm flex items-center gap-1"><Download size={14} /> İndir</a>
                )}
              </div>
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div className="p-3 bg-[#0A0A0A] rounded-md border border-[#1A1A1A]">
                  <p className="text-xs text-[#525252] font-ibm mb-1">Meta Veriler</p>
                  {Object.entries(selected.metadata).filter(([k]) => k !== 'job_id').map(([k, v]) => (
                    <span key={k} className="inline-block mr-2 text-xs font-jetbrains text-[#737373]">{k}: {String(v).slice(0, 50)}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
